// src/pages/FastScan.js
// Fast Scan Page - Terminal Theme with Tier-Aware Features
// DriftAlarm.AI - Built by Security Experts

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { scanService } from '../services/api';
import tierService from '../services/tierService';
import { track as trackTelemetry } from '../services/telemetryService';
import CleanModernLayout from '../components/layouts/CleanModernLayout';
import { HelpIcon } from '../components/common';
import './FastScan.css';

// Attack surface coverage areas - base recon (available to all)
const RECON_COVERAGE = [
  { icon: '⚡', name: 'Top 100 Ports' },
  { icon: '🔐', name: 'SSL/TLS Certs' },
  { icon: '📡', name: 'DNS Records' },
  { icon: '🌐', name: 'HTTP Analysis' },
  { icon: '⚙️', name: 'Tech Stack' },
  { icon: '🔍', name: 'Header Analysis' },
  { icon: '🤖', name: 'AI Analysis' }
];

// Standard tier features - SCRUTINIZE Deep Scan
const STANDARD_FEATURES = [
  { icon: '🔬', name: 'Exhaustive DNS Analysis' },
  { icon: '🕸️', name: 'Deep Web Crawling' },
  { icon: '💀', name: 'Complete Vulnerability Scanning' },
  { icon: '📅', name: 'Scheduled Scans' }
];

// Pro tier features - Continuous Monitoring & Validation
const PRO_FEATURES = [
  { icon: '🛡️', name: 'Continuous Asset Monitoring' },
  { icon: '✓', name: 'Vulnerability Validation' },
  { icon: '📊', name: 'Weekly & Monthly Reports' },
  { icon: '🎯', name: 'Proof of Concept Testing' }
];

// What You'll Learn badges
const LEARN_BADGES = [
  { label: 'Subdomains', desc: 'Discover hidden assets', iconClass: 'fs-icon-globe' },
  { label: 'Open Ports', desc: 'Find exposed services', iconClass: 'fs-icon-lock' },
  { label: 'Technologies', desc: 'Identify your stack', iconClass: 'fs-icon-gear' },
  { label: 'SSL/TLS', desc: 'Check certificate health', iconClass: 'fs-icon-shield' }
];

// Phased progress during scan
const SCAN_PHASES = [
  { label: 'Discovering subdomains...', pct: 10 },
  { label: 'Scanning open ports...', pct: 30 },
  { label: 'Detecting technologies...', pct: 55 },
  { label: 'Checking SSL certificates...', pct: 75 },
  { label: 'Analyzing security posture...', pct: 90 }
];

// Domain validation regex
const DOMAIN_REGEX = /^[a-zA-Z0-9]([a-zA-Z0-9-]*\.)+[a-zA-Z]{2,}$/;

// Strip protocol/www/trailing slash from pasted URLs
const cleanDomainInput = (raw) => {
  let cleaned = raw.trim();
  cleaned = cleaned.replace(/^https?:\/\//i, '');
  cleaned = cleaned.replace(/^www\./i, '');
  cleaned = cleaned.replace(/\/+$/, '');
  cleaned = cleaned.replace(/\/.*$/, '');
  return cleaned;
};

function FastScan() {
  const navigate = useNavigate();
  const lastDomain = localStorage.getItem('driftalarm_lastDomain') || '';
  const [target, setTarget] = useState('scanme.nmap.org');
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [domainValid, setDomainValid] = useState(null);

  // Phased progress state
  const [scanPhase, setScanPhase] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [scanComplete, setScanComplete] = useState(false);
  const timerRef = useRef(null);

  // Tier-aware state
  const [tierInfo, setTierInfo] = useState(null);

  // Load tier info on mount
  useEffect(() => {
    loadTierInfo();
  }, []);

  // Elapsed timer during scan
  useEffect(() => {
    if (scanning && !scanComplete) {
      timerRef.current = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [scanning, scanComplete]);

  // Phase transitions based on elapsed time
  useEffect(() => {
    if (scanComplete) {
      setScanPhase(SCAN_PHASES.length - 1);
      return;
    }
    if (elapsedSeconds < 20) setScanPhase(0);
    else if (elapsedSeconds < 40) setScanPhase(1);
    else if (elapsedSeconds < 60) setScanPhase(2);
    else if (elapsedSeconds < 80) setScanPhase(3);
    else setScanPhase(4);
  }, [elapsedSeconds, scanComplete]);

  const loadTierInfo = async () => {
    try {
      const info = await tierService.getCachedTierInfo();
      setTierInfo(info);
    } catch (err) {
      console.error('Failed to load tier info:', err);
    }
  };

  const canUseDeepScan = tierInfo?.features?.includeVulnCheck || false;
  const tier = tierInfo?.tier || 'free';

  // Usage limits
  const fastScanLimit = tierInfo?.limits?.fastScans?.limit || 10;
  const fastScanUsed = tierInfo?.limits?.fastScans?.used || 0;
  const fastScanUnlimited = tierInfo?.limits?.fastScans?.unlimited || false;

  // Domain input handler with auto-strip and validation
  const handleDomainChange = useCallback((e) => {
    const raw = e.target.value;
    const cleaned = cleanDomainInput(raw);
    setTarget(cleaned);
    if (cleaned.length === 0) {
      setDomainValid(null);
    } else {
      setDomainValid(DOMAIN_REGEX.test(cleaned));
    }
  }, []);

  const handleDomainPaste = useCallback((e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    const cleaned = cleanDomainInput(pasted);
    setTarget(cleaned);
    if (cleaned.length === 0) {
      setDomainValid(null);
    } else {
      setDomainValid(DOMAIN_REGEX.test(cleaned));
    }
  }, []);

  const handleScan = async () => {
    if (!target.trim()) {
      setError('Please enter a target IP or domain');
      return;
    }

    // Check usage limits
    if (!fastScanUnlimited && fastScanUsed >= fastScanLimit) {
      setError(`Weekly scan limit reached (${fastScanUsed}/${fastScanLimit}). Upgrade for more scans.`);
      // PRD-151 WP2: tier-gate block telemetry (enums only, no target/domain).
      try { trackTelemetry('action', { meta: { feature: 'scan', action: 'tier_limit_hit' } }); } catch (err) { /* best-effort */ }
      return;
    }

    // PRD-151 WP7: scan actually kicked off (tier-gate above passed). Enum-only
    // meta — NEVER the target/domain.
    try { trackTelemetry('scan_start', { meta: { scanType: 'fast' } }); } catch (err) { /* best-effort */ }

    console.log('Starting scan for:', target);
    setScanning(true);
    setError(null);
    setElapsedSeconds(0);
    setScanPhase(0);
    setScanComplete(false);

    // Save last scanned domain
    localStorage.setItem('driftalarm_lastDomain', target);

    try {
      // Start the scan
      const scanResponse = await scanService.startFastScan(target, {
        includeVulnCheck: canUseDeepScan,
        includeValidation: false // Validation happens post-scan
      });

      const scanId = scanResponse.scanId;

      // Clear tier cache to refresh usage counts
      tierService.clearTierCache();

      // Poll for results
      let attempts = 0;
      const maxAttempts = 60;

      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          const statusResponse = await scanService.getScanStatus(scanId);

          if (statusResponse.status === 'complete') {
            clearInterval(pollInterval);
            // PRD-151 WP7: fast scan resolved. Enum-only meta — no scanId/target.
            try { trackTelemetry('scan_complete', { meta: { scanType: 'fast' } }); } catch (err) { /* best-effort */ }
            setScanComplete(true);
            if (timerRef.current) clearInterval(timerRef.current);
            // Brief pause to show 100% then navigate
            setTimeout(() => navigate(`/scan-result/${scanId}`), 600);
          } else if (statusResponse.status === 'failed' || attempts >= maxAttempts) {
            clearInterval(pollInterval);
            if (timerRef.current) clearInterval(timerRef.current);
            setError('Scan failed or timed out. Please try again.');
            setScanning(false);
          }
        } catch (err) {
          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            if (timerRef.current) clearInterval(timerRef.current);
            setError('Failed to retrieve scan results');
            setScanning(false);
          }
        }
      }, 5000);

    } catch (err) {
      console.error('Scan error:', err);
      if (timerRef.current) clearInterval(timerRef.current);
      setError(`Failed to start scan: ${err.message}`);
      setScanning(false);
    }
  };

  const handleUpgrade = () => {
    // PRD-151 WP2: upgrade CTA telemetry (enum only, no plan chosen yet here).
    try { trackTelemetry('action', { meta: { feature: 'billing', action: 'upgrade_click' } }); } catch (err) { /* best-effort */ }
    navigate('/upgrade');
  };

  const progressPct = scanComplete ? 100 : SCAN_PHASES[scanPhase]?.pct || 0;

  return (
    <CleanModernLayout>
    <div className="fastscan-container">
      {/* Main Content */}
      <div className="fastscan-content">
        <div className="fastscan-card">
          {/* Terminal Header - Page Title */}
          <div className="terminal-header">
            <span className="terminal-icon">⚡</span>
            <span className="terminal-title">FAST SCAN<HelpIcon content="Fast Scan performs a quick reconnaissance of your target including port scanning, subdomain enumeration, and technology detection. Results in 2–5 minutes." category="context" /></span>
          </div>

          {/* Card Body */}
          <div className="card-body">
            {/* Status Indicator - Only show during scan */}
            {scanning && (
              <div className="status-indicator">
                <div className="status-dot active"></div>
                <span className="status-text">SCAN IN PROGRESS</span>
              </div>
            )}

            {/* Usage Counter - Show remaining scans */}
            {tier === 'free' && (
              <div className="fs-usage-counter">
                <span>{fastScanUsed} of {fastScanLimit} free scans this week</span>
                {!fastScanUnlimited && fastScanUsed >= fastScanLimit && (
                  <span className="fs-usage-upgrade" onClick={handleUpgrade}>
                    Upgrade for unlimited scans
                  </span>
                )}
              </div>
            )}

            {/* Scan Form */}
            <div className="scan-form">
              <div className="input-group">
                <label className="input-label" htmlFor="target">
                  TARGET DOMAIN OR IP
                </label>
                <div className="fs-input-wrapper">
                  <input
                    id="target"
                    type="text"
                    className="target-input"
                    placeholder={lastDomain ? `Last scan: ${lastDomain}` : 'Enter target domain or IP'}
                    value={target}
                    onChange={handleDomainChange}
                    onPaste={handleDomainPaste}
                    disabled={scanning}
                    onKeyDown={(e) => e.key === 'Enter' && !scanning && handleScan()}
                  />
                  {domainValid !== null && (
                    <span className={`fs-validation-icon ${domainValid ? 'valid' : 'invalid'}`}>
                      {domainValid ? '\u2713' : '\u2717'}
                    </span>
                  )}
                </div>
                <p className="input-hint">
                  Default: scanme.nmap.org (safe test site)
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  <span>{error}</span>
                </div>
              )}

              {/* Submit Button */}
              <button
                className="submit-button"
                onClick={handleScan}
                disabled={scanning || (!fastScanUnlimited && fastScanUsed >= fastScanLimit)}
              >
                {scanning ? (
                  <>
                    <div className="button-spinner"></div>
                    SCANNING...
                  </>
                ) : (
                  <>
                    ⚡ INITIATE FAST SCAN
                  </>
                )}
              </button>
            </div>

            {/* Brief Description */}
            <p className="card-description">
              Rapid security reconnaissance with essential checks.
              {tier === 'free' && (
                <span> Upgrade to Standard for deep scanning and schedules.</span>
              )}
            </p>

            {/* Phased Progress Indicator */}
            {scanning && (
              <div className="scanning-status">
                <div className="fs-progress-container">
                  <div className="fs-progress-phase">
                    {scanComplete ? 'Scan complete!' : SCAN_PHASES[scanPhase]?.label}
                  </div>
                  <div className="fs-progress-bar-track">
                    <div
                      className="fs-progress-bar-fill"
                      style={{ width: `${progressPct}%` }}
                    ></div>
                  </div>
                  <div className="scan-info">
                    <div className="info-row">
                      <span className="info-label">TARGET</span>
                      <span className="info-value mono">{target}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">ELAPSED</span>
                      <span className="info-value">{elapsedSeconds}s</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">EST. TIME</span>
                      <span className="info-value">2–5 min</span>
                    </div>
                  </div>
                </div>

                <div className="scanning-note">
                  <p><strong>Please wait...</strong> Do not close this page.</p>
                  <p>Results will appear automatically when scan completes.</p>
                </div>
              </div>
            )}

            {/* What You'll Learn Panel */}
            {!scanning && (
              <div className="fs-learn-panel">
                {LEARN_BADGES.map((badge) => (
                  <div key={badge.label} className="fs-learn-badge">
                    <div className={`fs-learn-badge-icon ${badge.iconClass}`}></div>
                    <div className="fs-learn-badge-label">{badge.label}</div>
                    <div className="fs-learn-badge-desc">{badge.desc}</div>
                  </div>
                ))}
                <div className="fs-learn-explainer">
                  Run a free scan to discover what attackers can see about your domain.
                </div>
              </div>
            )}

            {/* Coverage & Upgrade Sections - Free tier only */}
            {!scanning && tier === 'free' && (
              <div className="coverage-section">
                {/* Base Recon - Always Available */}
                <div className="coverage-group">
                  <span className="coverage-group-label">Fast Scan Coverage</span>
                  <div className="coverage-grid">
                    {RECON_COVERAGE.map((area) => (
                      <div key={area.name} className="coverage-item">
                        <span className="coverage-icon">{area.icon}</span>
                        <span className="coverage-text">{area.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Standard Upgrade - SCRUTINIZE Deep Scan */}
                <div className="upgrade-section standard">
                  <div className="upgrade-header">
                    <span className="upgrade-icon">🔬</span>
                    <span className="upgrade-title">DEEP SCAN</span>
                    <span className="upgrade-tier-badge">Standard</span>
                  </div>
                  <div className="upgrade-features">
                    {STANDARD_FEATURES.map((feature) => (
                      <div key={feature.name} className="upgrade-feature">
                        <span className="feature-icon">{feature.icon}</span>
                        <span className="feature-text">{feature.name}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    className="unlock-button"
                    onClick={handleUpgrade}
                  >
                    Unlock with Standard
                  </button>
                </div>

                {/* Pro Upgrade - Continuous Monitoring & Validation */}
                <div className="upgrade-section pro">
                  <div className="upgrade-header">
                    <span className="upgrade-icon">🛡️</span>
                    <span className="upgrade-title">Continuous Monitoring & Validation</span>
                    <span className="upgrade-tier-badge pro">Pro</span>
                  </div>
                  <div className="upgrade-features">
                    {PRO_FEATURES.map((feature) => (
                      <div key={feature.name} className="upgrade-feature">
                        <span className="feature-icon">{feature.icon}</span>
                        <span className="feature-text">{feature.name}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    className="unlock-button pro"
                    onClick={handleUpgrade}
                  >
                    Unlock with Pro
                  </button>
                </div>
              </div>
            )}

            {/* Coverage for paid tiers */}
            {!scanning && tier !== 'free' && (
              <div className="coverage-section">
                <div className="section-label">Attack Surface Coverage</div>
                <div className="coverage-group">
                  <div className="coverage-grid">
                    {RECON_COVERAGE.map((area) => (
                      <div key={area.name} className="coverage-item">
                        <span className="coverage-icon">{area.icon}</span>
                        <span className="coverage-text">{area.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Info Sidebar — What Fast Scan Does */}
        <div className="fastscan-info-panel">
          <div className="info-section">
            <h3 className="info-title">What Fast Scan Does</h3>
            <div className="info-details">
              <div className="detail-row">
                <span className="detail-icon included">⚡</span>
                <span className="detail-label">2–5 min</span>
                <span className="detail-value">Rapid reconnaissance</span>
              </div>
              <div className="detail-row">
                <span className="detail-icon included">🔍</span>
                <span className="detail-label">Top 100 Ports</span>
                <span className="detail-value">Service detection</span>
              </div>
              <div className="detail-row">
                <span className="detail-icon included">🌐</span>
                <span className="detail-label">URL Enumeration</span>
                <span className="detail-value">Web surface mapping</span>
              </div>
              <div className="detail-row">
                <span className="detail-icon included">🛡️</span>
                <span className="detail-label">Vuln Scan</span>
                <span className="detail-value">Known CVEs + misconfigs</span>
              </div>
              <div className="detail-row">
                <span className="detail-icon included">🤖</span>
                <span className="detail-label">AI Action Plan</span>
                <span className="detail-value">Prioritized remediation</span>
              </div>
              <div className="detail-row">
                <span className="detail-icon included">📊</span>
                <span className="detail-label">Risk Score</span>
                <span className="detail-value">AI-weighted assessment</span>
              </div>
            </div>
          </div>

          <div className="info-section">
            <h3 className="info-title">Need More Depth?<HelpIcon content="Deep Scan performs exhaustive vulnerability testing including web application scanning, SSL/TLS analysis, and advanced security checks. Takes 15-30 minutes." category="context" /></h3>
            <p className="info-text">
              Deep Scan runs exhaustive DNS enumeration,
              web crawling, and full vulnerability testing.
            </p>
            <button
              className="secondary-button"
              onClick={() => navigate('/comprehensive-scan')}
            >
              Go to Deep Scan
            </button>
          </div>
        </div>
      </div>
    </div>
    </CleanModernLayout>
  );
}

export default FastScan;
